import { supabase } from '../lib/supabase';

class TestesService {
  // Buscar todos os testes com dados relacionados
  async getAll() {
    try {
      const { data, error } = await supabase
        .from('testes')
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar testes:', error);
      throw error;
    }
  }

  // Buscar teste por ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar teste:', error);
      throw error;
    }
  }

  // Criar novo teste
  async create(testeData) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .insert([{
          ...testeData,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao criar teste:', error);
      throw error;
    }
  }

  // Atualizar teste
  async update(id, testeData) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .update(testeData)
        .eq('id', id)
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar teste:', error);
      throw error;
    }
  }

  // Excluir teste
  async delete(id) {
    try {
      const { error } = await supabase
        .from('testes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Erro ao excluir teste:', error);
      throw error;
    }
  }

  // Ativar/Desativar teste
  async toggleActive(id, ativo) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .update({ ativo })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao alterar status do teste:', error);
      throw error;
    }
  }

  // Alterar status do teste
  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao alterar status do teste:', error);
      throw error;
    }
  }

  // Buscar matérias para dropdown
  async getMaterias() {
    try {
      const { data, error } = await supabase
        .from('materias')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar matérias:', error);
      throw error;
    }
  }

  // Buscar anos escolares para dropdown
  async getAnosEscolares() {
    try {
      const { data, error } = await supabase
        .from('anos_escolares')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar anos escolares:', error);
      throw error;
    }
  }

  // Buscar escolas para dropdown
  async getEscolas() {
    try {
      const { data, error } = await supabase
        .from('escolas')
        .select('id, nome')
        .order('nome');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar escolas:', error);
      throw error;
    }
  }

  // Buscar estatísticas do teste
  async getEstatisticas(testeId) {
    try {
      const { data, error } = await supabase
        .rpc('calcular_estatisticas_teste', { teste_uuid: testeId });

      if (error) throw error;
      return data[0] || {
        total_participantes: 0,
        participantes_finalizados: 0,
        media_pontuacao: 0,
        taxa_aprovacao: 0
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      return {
        total_participantes: 0,
        participantes_finalizados: 0,
        media_pontuacao: 0,
        taxa_aprovacao: 0
      };
    }
  }

  // Filtrar testes
  async filter(filters = {}) {
    try {
      let query = supabase
        .from('testes')
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `);

      // Aplicar filtros
      if (filters.materia_id) {
        query = query.eq('materia_id', filters.materia_id);
      }

      if (filters.ano_escolar_id) {
        query = query.eq('ano_escolar_id', filters.ano_escolar_id);
      }

      if (filters.escola_id) {
        query = query.eq('escola_id', filters.escola_id);
      }

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.tipo_teste) {
        query = query.eq('tipo_teste', filters.tipo_teste);
      }

      if (filters.ativo !== undefined) {
        query = query.eq('ativo', filters.ativo);
      }

      if (filters.search) {
        query = query.or(`nome.ilike.%${filters.search}%,descricao.ilike.%${filters.search}%`);
      }

      if (filters.data_inicio && filters.data_fim) {
        query = query.gte('data_teste', filters.data_inicio)
                    .lte('data_teste', filters.data_fim);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao filtrar testes:', error);
      throw error;
    }
  }

  // Duplicar teste
  async duplicate(id) {
    try {
      // Buscar teste original
      const original = await this.getById(id);
      
      // Remover campos que não devem ser duplicados
      const { id: _, created_at, updated_at, ...testeData } = original;
      
      // Modificar nome para indicar duplicação
      testeData.nome = `${testeData.nome} (Cópia)`;
      testeData.status = 'rascunho';
      
      // Criar novo teste
      return await this.create(testeData);
    } catch (error) {
      console.error('Erro ao duplicar teste:', error);
      throw error;
    }
  }

  // Buscar testes por período
  async getByPeriod(dataInicio, dataFim) {
    try {
      const { data, error } = await supabase
        .from('testes')
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .gte('data_teste', dataInicio)
        .lte('data_teste', dataFim)
        .order('data_teste');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar testes por período:', error);
      throw error;
    }
  }

  // Buscar testes agendados
  async getAgendados() {
    try {
      const { data, error } = await supabase
        .from('testes')
        .select(`
          *,
          materias:materia_id(id, nome),
          anos_escolares:ano_escolar_id(id, nome),
          escolas:escola_id(id, nome)
        `)
        .eq('status', 'agendado')
        .gte('data_teste', new Date().toISOString().split('T')[0])
        .order('data_teste');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar testes agendados:', error);
      throw error;
    }
  }
}

export default new TestesService();
